/**
 * Facility List Component
 *
 * Displays a list of facilities with search, filtering, and management capabilities.
 * Uses reusable components for toolbar, status toggle, and data display.
 *
 * @component
 */

import React, { useState, useEffect } from "react";
import { useSmartOSearch } from "oreacto";
import { useSearchParams } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import WarningIcon from "@mui/icons-material/Warning";
import Box from "@mui/material/Box";
import StickyFlexTable from "../common/StickyFlexTable";
import ThemedTooltip from "../common/ThemedTooltip";
import ThemedChip from "../common/ThemedChip";
import ManagementToolbar from "../common/ManagementToolbar";
import StatusToggleButton from "../common/StatusToggleButton";
import StatusToggleDialog from "../common/StatusToggleDialog";
import ListModal from "../common/ListModal";
import { useTheme } from "../../ThemeContext";
import IconButton from "@mui/material/IconButton";
import { formatPhoneNumberForList } from "../../utils/helpers/format-phone-number";
import { useDetectMyRole } from "../../custom-hooks";
import { useStatusToggle } from "../../custom-hooks";
import FacilityService from "../../services/facility-service";
import { getFacilitiesWithUnassignedStaff } from "../../utils/apiClient";
import {
  FACILITY_FILTER_GROUPS,
  SEARCH_PLACEHOLDERS,
  MODULE_PERMISSIONS,
} from "../../constants/filter-constants";

/**
 * FacilityList Component
 *
 * @param {Object} props - Component props
 * @param {Function} props.setRenderType - Function to change render type
 * @param {string} props.renderType - Current render type
 * @param {Array} props.facilities - Array of facility objects
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onEdit - Edit handler
 * @param {Function} props.onAdd - Add new facility handler
 * @param {Function} props.onRefresh - Refresh handler
 * @param {Object} props.selectedFilters - Current filter state
 * @param {Function} props.onFiltersChange - Filter change handler
 * @returns {JSX.Element} FacilityList component
 */
const FacilityList = ({
  setRenderType,
  renderType,
  facilities = [],
  loading,
  onEdit,
  onAdd,
  onRefresh,
  selectedFilters,
  onFiltersChange,
  // Legacy props (not used anymore but kept for compatibility)
  setInitialValues,
  setSelectedFacilityId,
  onDelete,
}) => {
  const { currentTheme } = useTheme();
  const { role } = useDetectMyRole();
  const [, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [plansModalOpen, setPlansModalOpen] = useState(false);
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [selectedFacilityName, setSelectedFacilityName] = useState("");
  const [planTypesModalOpen, setPlanTypesModalOpen] = useState(false);
  const [selectedPlanTypes, setSelectedPlanTypes] = useState([]);

  // State for facilities requiring action (dynamic from API)
  const [facilitiesRequiringAction, setFacilitiesRequiringAction] = useState(
    []
  );
  const [loadingActionFacilities, setLoadingActionFacilities] = useState(true);

  // Use status toggle hook
  const {
    isDialogOpen: statusDialogOpen,
    isLoading: statusLoading,
    entityToToggle: facilityToToggle,
    toggleAction,
    handleToggleClick,
    handleConfirm: handleStatusToggleConfirm,
    handleCancel: handleStatusToggleCancel,
  } = useStatusToggle(FacilityService.toggleStatus, onRefresh, "facility");

  // Check permissions
  const canEditFacility = MODULE_PERMISSIONS.facility.edit.includes(role);

  /**
   * Apply filters to facilities based on selected filters
   *
   * @param {Array} facilitiesData - Raw facilities data
   * @returns {Array} Filtered facilities
   */
  const applyFilters = (facilitiesData) => {
    return facilitiesData.filter((facility) => {
      const statusFilter = selectedFilters?.status;
      if (statusFilter === "active" && facility.isActive !== true) return false;
      if (statusFilter === "inactive" && facility.isActive !== false)
        return false;
      return true;
    });
  };

  // Preprocess facilities to flatten nested fields for search
  const flatFacilities = facilities.map((f) => ({
    ...f,
    city: f.location?.city || "",
    state: f.location?.state || "",
    address: f.location
      ? `${f.location.streetAddress || ""} ${f.location.city || ""} ${f.location.state || ""} ${f.location.zipcode || ""}`.trim()
      : "",
  }));

  // Apply filters to facilities
  const filteredFacilitiesData = applyFilters(flatFacilities);

  // Use smart search hook
  const { filteredItems, query, setQuery } = useSmartOSearch({
    items: filteredFacilitiesData,
    filterKeys: ["facilityName", "phoneNumber", "groupNPI", "groupTaxId", "address"],
    searchQuery: "",
    sortKey: "facilityName",
    sortOrder: "asc",
  });

  // Handle search with comma removal for flexible address matching
  const handleSearchChange = (value) => setQuery((value || "").replace(/,/g, ""));

  /**
   * Reset pagination to first page when filters or search query change
   * This ensures user doesn't stay on a page that no longer exists
   */
  useEffect(() => {
    setPage(0);
  }, [selectedFilters, query]);

  /**
   * Load facilities requiring action when component mounts
   */
  useEffect(() => {
    fetchFacilitiesRequiringAction();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Check if a facility requires action
   *
   * @param {string} facilityId - The facility ID to check
   * @returns {boolean} True if facility requires action
   */
  const requiresAction = (facilityId) => {
    return facilitiesRequiringAction.includes(facilityId);
  };

  // Sort facilities to prioritize those requiring action at the top
  const sortedFacilities = (
    query ? filteredItems : filteredFacilitiesData
  ).sort((a, b) => {
    const aRequiresAction = requiresAction(a._id);
    const bRequiresAction = requiresAction(b._id);

    // Facilities requiring action come first
    if (aRequiresAction && !bRequiresAction) return -1;
    if (!aRequiresAction && bRequiresAction) return 1;

    // Within each group (action required vs not required), maintain alphabetical order by facility name
    return (a.facilityName || "").localeCompare(b.facilityName || "");
  });

  const filteredFacilities = sortedFacilities;

  /**
   * Renders plan types with show more functionality
   *
   * @param {string|Array} planTypes - Plan type data
   * @param {string} facilityName - Facility name for modal
   * @returns {JSX.Element} Rendered plan types
   */
  const renderPlanType = (planTypes, facilityName) => {
    if (!Array.isArray(planTypes)) {
      // Handle single string or non-array values
      return planTypes || <span style={{ fontSize: 13, opacity: 0.7 }}>No plan types listed</span>;
    }

    if (planTypes.length === 0) {
      return (
        <span style={{ fontSize: 13, opacity: 0.7 }}>No plan types listed</span>
      );
    }

    if (planTypes.length <= 3) {
      return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {planTypes.map((type, idx) => (
            <ThemedChip key={idx} variant="default" size="small">
              {type}
            </ThemedChip>
          ))}
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          alignItems: "center",
        }}
      >
        {planTypes.slice(0, 2).map((type, idx) => (
          <ThemedChip key={idx} variant="default" size="small">
            {type}
          </ThemedChip>
        ))}
        <ThemedTooltip title="Click to see all plan types" placement="top">
          <ThemedChip
            variant="info"
            size="small"
            onClick={() => handleShowAllPlanTypes(planTypes, facilityName)}
            sx={{ cursor: "pointer" }}
          >
            +{planTypes.length - 2} more
          </ThemedChip>
        </ThemedTooltip>
      </Box>
    );
  };

  /**
   * Handles showing all plans in modal
   *
   * @param {Array} plans - Insurance plans
   * @param {string} facilityName - Facility name
   */
  const handleShowAllPlans = (plans, facilityName) => {
    setSelectedPlans(plans);
    setSelectedFacilityName(facilityName);
    setPlansModalOpen(true);
  };

  /**
   * Handles showing all plan types in modal
   *
   * @param {Array} planTypes - Insurance plan types
   * @param {string} facilityName - Facility name
   */
  const handleShowAllPlanTypes = (planTypes, facilityName) => {
    setSelectedPlanTypes(planTypes);
    setSelectedFacilityName(facilityName);
    setPlanTypesModalOpen(true);
  };

  /**
   * Renders insurance plans with show more functionality
   *
   * @param {Array} plans - Insurance plans
   * @param {string} facilityName - Facility name for modal
   * @returns {JSX.Element} Rendered plans
   */
  const renderPlans = (plans, facilityName) => {
    if (!Array.isArray(plans) || plans.length === 0) {
      return (
        <span style={{ fontSize: 13, opacity: 0.7 }}>No plans listed</span>
      );
    }

    if (plans.length <= 3) {
      return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {plans.map((plan, idx) => (
            <ThemedChip key={idx} variant="default" size="small">
              {plan}
            </ThemedChip>
          ))}
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          alignItems: "center",
        }}
      >
        {plans.slice(0, 2).map((plan, idx) => (
          <ThemedChip key={idx} variant="default" size="small">
            {plan}
          </ThemedChip>
        ))}
        <ThemedTooltip title="Click to see all plans" placement="top">
          <ThemedChip
            variant="info"
            size="small"
            onClick={() => handleShowAllPlans(plans, facilityName)}
            sx={{ cursor: "pointer" }}
          >
            +{plans.length - 2} more
          </ThemedChip>
        </ThemedTooltip>
      </Box>
    );
  };

  /**
   * Handles adding a new facility
   */
  const handleAddFacility = () => {
    // Use the onAdd prop which properly clears state via CRUD operations
    if (onAdd) {
      onAdd();
    } else {
      // Fallback to legacy behavior
      setRenderType("form");
      if (setInitialValues) setInitialValues(null);
      if (setSelectedFacilityId) setSelectedFacilityId(null);
    }
  };

  /**
   * Fetch facilities with unassigned staff from API
   */
  const fetchFacilitiesRequiringAction = async () => {
    try {
      setLoadingActionFacilities(true);
      const response = await getFacilitiesWithUnassignedStaff();

      if (
        response.statusCode === "SUCCESS" &&
        response.response &&
        typeof response.response === "object"
      ) {
        const {
          facilities_with_unassigned_staff = [],
          facilities_with_unassigned_rendering_providers = [],
        } = response.response;

        // Combine both arrays and remove duplicates
        const combinedFacilityIds = [
          ...facilities_with_unassigned_staff,
          ...facilities_with_unassigned_rendering_providers,
        ];

        // Remove duplicates using Set
        const uniqueFacilityIds = [...new Set(combinedFacilityIds)];

        setFacilitiesRequiringAction(uniqueFacilityIds);
        console.log(
          "✅ Loaded facilities requiring action:",
          uniqueFacilityIds.length
        );
        console.log(
          "📊 Staff issues:",
          facilities_with_unassigned_staff.length,
          "Provider issues:",
          facilities_with_unassigned_rendering_providers.length
        );
      } else {
        console.warn(
          "⚠️ Unexpected response format for facilities with unassigned resources:",
          response
        );
        setFacilitiesRequiringAction([]);
      }
    } catch (error) {
      console.error(
        "❌ Error fetching facilities with unassigned resources:",
        error
      );
      setFacilitiesRequiringAction([]);
    } finally {
      setLoadingActionFacilities(false);
    }
  };

  /**
   * Handles warning button click to navigate to department allocation
   *
   * @param {string} facilityId - The facility ID to set in URL parameters
   */
  const handleWarningClick = (facilityId) => {
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.set("departmentAllocation", "true");
      newParams.set("facilityId", facilityId);
      return newParams;
    });
  };

  // Table columns configuration
  const columns = [
    {
      id: "facilityName",
      label: "Name",
      width: 150,
      fixed: true,
      renderCell: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <span>{row.facilityName}</span>
          {!loadingActionFacilities && requiresAction(row._id) && (
            <ThemedTooltip
              title="Action required for this facility - Click to manage departments"
              placement="top"
            >
              <IconButton
                aria-label="action required"
                onClick={() => handleWarningClick(row._id)}
                size="small"
                sx={{
                  color: currentTheme.colors?.warning || "#ff9800",
                  cursor: "pointer",
                  padding: "2px",
                }}
              >
                <WarningIcon fontSize="small" />
              </IconButton>
            </ThemedTooltip>
          )}
        </Box>
      ),
    },
    {
      id: "phoneNumber",
      label: "Phone No",
      width: 160,
      renderCell: (row) => (
        <ThemedTooltip
          title={row.extension ? `Extension: ${row.extension}` : ""}
          placement="top"
        >
          <span>{formatPhoneNumberForList(row.phoneNumber)}</span>
        </ThemedTooltip>
      ),
    },
    {
      id: "address",
      label: "Address",
      width: 150,
      renderCell: (row) => row.address,
    },
    {
      id: "faxNumber",
      label: "Fax No",
      width: 160,
      renderCell: (row) => formatPhoneNumberForList(row.faxNumber),
    },
    {
      id: "groupNPI",
      label: "Group NPI",
      minWidth: 120,
      renderCell: (row) => row.groupNPI,
    },
    {
      id: "groupTaxId",
      label: "Group Tax ID",
      minWidth: 120,
      renderCell: (row) => row.groupTaxId,
    },
    {
      id: "inNetworkInsurancePlans",
      label: "In Network Ins. Plans",
      minWidth: 220,
      renderCell: (row) =>
        renderPlans(row.inNetworkInsurancePlans, row.facilityName),
    },
    {
      id: "inNetworkInsurancePlanType",
      label: "Network Ins. Plan Type",
      minWidth: 180,
      renderCell: (row) => renderPlanType(row.inNetworkInsurancePlanType, row.facilityName),
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      renderCell: (row) => (
        <ThemedChip variant={row.isActive ? "success" : "warning"} size="small">
          {row.isActive ? "Active" : "Inactive"}
        </ThemedChip>
      ),
    },
    {
      id: "action",
      label: "Action",
      minWidth: 120,
      fixed: true,
      align: "right",
      renderCell: (row) => (
        <>
          {/* {!loadingActionFacilities && requiresAction(row._id) && (
            <ThemedTooltip
              title="Action required for this facility - Click to manage departments"
              placement="top"
            >
              <IconButton
                aria-label="action required"
                onClick={() => handleWarningClick(row._id)}
                size="small"
                sx={{
                  color: currentTheme.colors?.warning || "#ff9800",
                  cursor: "pointer",
                }}
              >
                <WarningIcon fontSize="small" />
              </IconButton>
            </ThemedTooltip>
          )} */}
          <ThemedTooltip
            title={
              canEditFacility
                ? "Edit facility information"
                : "Administrator privileges required to modify facility records"
            }
            placement="top"
          >
            <span>
              <IconButton
                aria-label="edit"
                onClick={() => canEditFacility && onEdit && onEdit(row)}
                size="small"
                disabled={!canEditFacility}
                sx={{
                  color: canEditFacility
                    ? currentTheme.text.primary
                    : currentTheme.text.disabled || "#999",
                  cursor: canEditFacility ? "pointer" : "not-allowed",
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </ThemedTooltip>
          <StatusToggleButton
            entity={row}
            module="facility"
            onToggle={handleToggleClick}
          />
        </>
      ),
    },
  ];

  // Transform facilities data for table
  const rows = filteredFacilities.map((f) => ({
    ...f,
    address: f.location
      ? `${f.location.streetAddress}, ${f.location.city}, ${f.location.state} ${f.location.zipcode}`
      : "",
  }));

  return (
    <>
      <StickyFlexTable
        columns={columns}
        rows={rows}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        toolbar={
          <ManagementToolbar
            module="facility"
            searchQuery={query}
            onSearchChange={handleSearchChange}
            searchPlaceholder={SEARCH_PLACEHOLDERS.facility}
            filterGroups={FACILITY_FILTER_GROUPS}
            selectedFilters={selectedFilters}
            onFiltersChange={onFiltersChange}
            onAdd={handleAddFacility}
            addButtonLabel="Add Facility"
          />
        }
      />

      {/* Status Toggle Dialog */}
      <StatusToggleDialog
        open={statusDialogOpen}
        entity={facilityToToggle}
        action={toggleAction}
        module="facility"
        onConfirm={handleStatusToggleConfirm}
        onCancel={handleStatusToggleCancel}
        loading={statusLoading}
      />

      {/* Insurance Plans Modal */}
      <ListModal
        open={plansModalOpen}
        title="Insurance Plans"
        subtitle={`Facility: ${selectedFacilityName}`}
        data={selectedPlans}
        onClose={() => setPlansModalOpen(false)}
        maxWidth={500}
        showIndex={true}
      />

      {/* Insurance Plan Types Modal */}
      <ListModal
        open={planTypesModalOpen}
        title="Insurance Plan Types"
        subtitle={`Facility: ${selectedFacilityName}`}
        data={selectedPlanTypes}
        onClose={() => setPlanTypesModalOpen(false)}
        maxWidth={400}
        showIndex={true}
      />
    </>
  );
};

export default FacilityList;
